/**
 * Fotos vor dem Hochladen verkleinern — ohne die Angaben zu verlieren.
 *
 * Der Server kann das nicht: ohne ffmpeg und ohne verlässliche GD-Ausstattung
 * bleibt ihm nur, entgegenzunehmen, was kommt. Also rechnet der Browser --
 * und das ist ohnehin die bessere Stelle, weil dann auch die Leitung
 * unterwegs nicht das volle Bild tragen muss.
 *
 * Ein iPhone-Foto von 3 MB wird so zu 300 bis 600 kB. Bei 40 GB freiem
 * Speicher ist das der Unterschied zwischen 13.000 und 90.000 Bildern.
 *
 * **Die EXIF-Daten bleiben erhalten**, allen voran der Aufnahmeort: sie
 * werden aus dem Original übernommen und in das verkleinerte Bild gesetzt
 * (siehe `exif.ts`). Bei einem Reisebericht ist der Ort die Angabe, nach der
 * man Jahre später sucht.
 *
 * Ein Foto, das ohnehin klein genug ist, wird **gar nicht angefasst** und
 * unverändert hochgeladen. Das erhält nicht nur alle Angaben, sondern auch
 * die volle Bildqualität — neu zeichnen kostet immer etwas.
 */

import { baueExif, findExif, isJpeg, istHeif, readAufnahme, uprightExif, withExif, ausrichtung } from './exif'
import type { Aufnahme } from './exif'
import type { Uhr } from './ortszeit'
import type { Settings } from '../api/types'
import { WORTLAUT } from '../texte'
import { isFullSphere, withSphereXmp } from './panorama'
import { metadataFromIso, namesGainMap, splitGainMap, withGainMap } from './gainMap'
import type { MapMetadata } from './gainMap'
import { heifGainMap, type HeifGainMap } from './heifGainMap'
import { canvasSpaceFor, contextSpace, hasIcc, iccOf, withIcc } from './colourProfile'
import { dngPreview, previewSuffices } from './dngPreview'
import type { CanvasSpace } from './colourProfile'
import { notiere } from './sendeprotokoll'

/**
 * Lange Kante und Qualität -- die Vorgaben, wenn der Server nichts sagt.
 *
 * Sie stehen hier als Rückfalllinie, nicht als Gesetz: die geltenden Werte
 * kommen aus den Einstellungen (`/api/settings`) und lassen sich im Betrieb
 * ändern, ohne die Oberfläche neu zu bauen. Nur solange diese Abfrage noch
 * unterwegs ist -- oder scheitert -- gelten diese zwei Zahlen.
 */
export const MAX_EDGE = 2560
export const QUALITY = 80

/** Bis hierher lohnt das Verkleinern nicht. */
const KEEP_ORIGINAL_BYTES = 4 * 1024 * 1024

/**
 * Ein GIF? Gelesen an den ersten Bytes (`GIF87a`/`GIF89a`), nicht am Namen
 * und nicht am Typ, den der Browser meldet -- beides kann jemand setzen, die
 * Datei nicht.
 */
function istGif(bytes: Uint8Array): boolean {
  return (
    bytes.length > 6
    && bytes[0] === 0x47 && bytes[1] === 0x49 && bytes[2] === 0x46
    && bytes[3] === 0x38 && (bytes[4] === 0x37 || bytes[4] === 0x39) && bytes[5] === 0x61
  )
}

export interface Prepared {
  blob: Blob
  width: number
  height: number
  /**
   * Wo und wann das Foto entstanden ist -- aus dem **Original** gelesen.
   *
   * Der Server bekommt es als Kopfzeile mitgeschickt und legt es an der Datei
   * ab. Er selbst kann es nicht lesen: EXIF auseinanderzunehmen ist Arbeit,
   * die auf einem geteilten Paket nichts zu suchen hat, und der Browser hat
   * die Datei ohnehin schon offen.
   */
  aufnahme: Aufnahme
  /**
   * Das Vorschaubild für die Kachel im Chat (#57) — oder `null`.
   *
   * **Warum es hier entsteht und nicht auf dem Server.** Auf dem Zielpaket
   * gibt es weder GD noch Imagick; hier liegt das Bild ohnehin schon auf einer
   * Leinwand, und ein zweiter Durchgang kostet Millisekunden.
   *
   * `null` bei einem GIF: der Weg unten schreibt ein JPEG, und aus einem
   * Zwinkern würde damit ein Standbild. Ein GIF ist ohnehin klein.
   */
  thumb: Blob | null
  /**
   * Whether the photo went out with its gain map (1.179.0, #155): an HDR
   * JPEG, reduced, with the map reduced beside it. The server reads the
   * same from the file; this is for the tile.
   */
  hdr: boolean
}

/**
 * Wie groß ein Vorschaubild wird, wenn die Einstellungen noch nicht da sind
 * — **800 Punkte auf der langen Kante** (1.4.0, vorher 400).
 *
 * Aus dem Betrieb, am iPad: „ziemlich grieselig". Die Kachel dort ist bis zu
 * 320 Punkte breit, bei doppelter Dichte 640 echte Bildpunkte; 400 wurde auf
 * das Doppelte gestreckt. Die Zahl steht seitdem in den Einstellungen
 * (`thumb_max_edge`) und kommt über `bildgrenzen()` hierher; dies ist nur
 * die Vorgabe für den Augenblick davor. Die Rechnung von damals darunter:
 *
 * Die Kachel im Chat ist rund 120 Punkte breit; auf einem Bildschirm mit
 * dreifacher Dichte sind das 360 echte Bildpunkte. 400 deckt das ab und ergibt
 * rund 30 Kilobyte — gegenüber einem halben bis anderthalb Megabyte, die die
 * Kachel bis #57 geladen hat.
 */
const THUMB_EDGE = 800

/**
 * Und mit welcher Güte. **Fünfundsiebzig statt der eingestellten Zahl:** ein
 * Bild, das auf einen Bruchteil seiner Kantenlänge geht, verträgt mehr
 * Kompression als eines, das jemand groß ansieht (bis 1.4.0 siebzig).
 */
const THUMB_QUALITY = 0.75

/** Die eingestellte Bildgröße; `quality` in Prozent, wie sie der Server führt. */
export interface ImageLimits {
  // Freiwillig: solange die Einstellungen noch unterwegs sind, gelten die
  // Vorgaben oben. `prepareImage()` setzt sie ohnehin selbst ein.
  maxEdge?: number
  quality?: number
  /** Die lange Kante des Vorschaubilds (1.4.0), aus `thumb_max_edge`. */
  thumbEdge?: number
  /**
   * The long edge of a 360° photo (1.173.0, `pano_max_edge`) -- given only
   * for the trip's author, who alone may show one (`bildgrenzen()`). Without
   * it a sphere is scaled and flagged like any photo, and the server shows it
   * flat.
   */
  panoEdge?: number
  /** The most bytes a photo may weigh (`image_max_mb`); a sphere lowers its quality to fit. */
  maxBytes?: number
  /**
   * Keep the gain map of an HDR JPEG when it is reduced (1.179.0, #155,
   * `hdr_photo`). Without it the map is dropped, also from a JPEG that
   * would have gone out unchanged: the switch says SDR.
   */
  hdrPhoto?: boolean
  /**
   * How the gain map of a HEIC is decoded (1.181.0): the browser's own HEIF
   * decoder by default. A door for the bench, whose Chromium decodes no HEIF;
   * the app never sets it.
   */
  heifDecode?: (heif: Uint8Array<ArrayBuffer>) => Promise<ImageBitmap>
  /**
   * Die Uhr der Reise — für Bilder, deren EXIF-Block keine Zone trägt.
   *
   * `DateTimeOriginal` ist Ortszeit ohne Zone. Ohne diese Angabe läse der
   * Browser sie in **seiner** Uhr, und in der Nachbearbeitung zu Hause landet
   * ein Foto damit Stunden neben seinem Platz. Die Reise weiß dagegen, wo die
   * Kamera stand.
   */
  reiseVersatz?: Uhr
}

/**
 * Welche Bildgrenzen für wen gelten (#59).
 *
 * **Eine Stelle, und nur eine.** Seit Mitlesende Fotos schicken dürfen, gibt
 * es zwei Paare in den Einstellungen — und die Frage „welches gilt hier?"
 * gehört genau einmal beantwortet. Stünde sie an jedem Aufrufer, liefe sie
 * beim dritten auseinander; das ist in diesem Projekt dreimal passiert.
 *
 * **Ohne Antwort des Servers gilt nichts von beidem**, sondern die Vorgabe in
 * `prepareImage()`. Ein Foto, das mit geratenen Grenzen verkleinert wurde,
 * sieht aus wie eines, das der Einstellung folgt.
 *
 * **Sechs Wege rufen sie** (1.1.1): der Composer nach der Rolle, die der
 * Server für die Reise nennt; Symbolbild, Chat-Hintergrund, Foto am Punkt und
 * Foto zu Punkt mit `true`, denn dort kommt nur der Autor hin. Vier davon
 * griffen das Paar vorher selbst ab — richtig, aber die Regel stand fünfmal.
 */
export function bildgrenzen(
  werte: Settings | null,
  istReisender: boolean,
): { maxEdge: number; quality: number; thumbEdge: number; panoEdge?: number; maxBytes?: number; hdrPhoto: boolean } | Record<string, never> {
  if (werte === null) return {}
  // The thumbnail's edge is one for both roles (1.4.0): a tile is a tile.
  const thumbEdge = werte.thumb_max_edge
  // HDR photos (1.179.0) are one switch for both roles, like the video's.
  const hdrPhoto = werte.hdr_photo === 'on'
  // A 360° photo is the author's (1.173.0); a reader's sphere stays a photo.
  return istReisender
    ? {
        maxEdge: werte.image_max_edge, quality: werte.image_quality, thumbEdge,
        panoEdge: werte.pano_max_edge, maxBytes: werte.image_max_mb * 1024 * 1024, hdrPhoto,
      }
    : { maxEdge: werte.reader_image_max_edge, quality: werte.reader_image_quality, thumbEdge, hdrPhoto }
}

/**
 * What a photo input asks the file picker for (1.181.0). With `hdr_photo` it
 * names HEIC explicitly: iOS otherwise hands a JPEG of its own instead of
 * the HEIC, and that is not known to carry the gain map. Whether iOS keeps
 * the original this way is read in the diagnosis ("Foto gewählt: …"), not
 * assumed. Without the switch nothing changes.
 */
export function photoTypes(werte: Settings | null | undefined, video = false): string {
  return 'image/*' + (werte?.hdr_photo === 'on' ? ',image/heic,image/heif' : '') + (video ? ',video/*' : '')
}

export async function prepareImage(file: File, limits?: ImageLimits): Promise<Prepared> {
  let maxEdge = limits?.maxEdge ?? MAX_EDGE
  const quality = (limits?.quality ?? QUALITY) / 100

  const original = new Uint8Array(await file.arrayBuffer())
  // Vor allem anderen: das Verkleinern rechnet die Bildpunkte neu, und der
  // Block wird nur nachträglich wieder eingesetzt. Aus dem Original gelesen,
  // ist die Angabe von dem, was der Fotoapparat geschrieben hat -- nicht von
  // dem, was danach noch übrig war.
  const aufnahme = readAufnahme(original, limits?.reiseVersatz ?? null)
  // **A RAW is drawn from its preview** (1.182.0, `lib/dngPreview.ts`), if
  // the preview is as large as the photo becomes. Since 1.182.1 also without
  // `hdr_photo`: then the base picture alone, SDR in Display P3, as for a
  // HEIC. `picture` is what the pixels, the profile and the gain map come
  // from; the capture data stay those of the original.
  const dng = dngPreview(original)
  const fromPreview = dng !== null && previewSuffices(dng, maxEdge)
  const picture = fromPreview ? dng.jpeg : original
  // **The canvas takes the colour space of the original** (1.180.0,
  // `lib/colourProfile.ts`): a Display P3 photo is drawn in P3 and goes out
  // with its profile; everything else in sRGB, as before.
  const profile = iccOf(picture)
  const space = canvasSpaceFor(profile)
  // **What arrived, in the diagnosis** (1.181.0): whether iOS hands over the
  // HEIC or a JPEG of its own, and whether a gain map came along, decides
  // whether an HDR photo can stay one. Measured on the device, not assumed.
  const heif = istHeif(original) ? heifGainMap(original) : null
  notiere(`Foto gewählt: ${file.type || 'ohne Typ'}, ${(original.byteLength / 1048576).toFixed(1)} MB, ${
    istHeif(original) ? (heif === null ? 'HEIC ohne verwendbare Gain Map' : `HEIC mit Gain Map ${heif.mapWidth}×${heif.mapHeight} und tmap`)
      : dng !== null ? `DNG mit Vorschau ${dng.width}×${dng.height} ${namesGainMap(dng.jpeg) ? 'mit' : 'ohne'} Gain Map, ${fromPreview ? 'gezeichnet aus der Vorschau' : 'gezeichnet aus dem RAW'}`
      : isJpeg(original) ? (namesGainMap(original) ? 'JPEG mit Gain Map' : 'JPEG ohne Gain Map') : 'weder JPEG noch HEIC'
  }, Profil ${profile === null ? 'keins' : space === 'display-p3' ? 'Display P3' : 'anderes'}`)
  const bitmap = await toBitmap(fromPreview ? new Blob([dng.jpeg as Uint8Array<ArrayBuffer>], { type: 'image/jpeg' }) : file)
  const longest = Math.max(bitmap.width, bitmap.height)
  // **A 360° photo keeps its own edge** (1.173.0): it is seen a quarter at a
  // time, and the edge of a flat photo would be four times too soft. Asked
  // only where the author uploads (`panoEdge`).
  const sphere = limits?.panoEdge !== undefined && isJpeg(original)
    && isFullSphere(original, bitmap.width, bitmap.height)
  if (sphere && limits?.panoEdge !== undefined) maxEdge = limits.panoEdge

  /**
   * **Ein GIF geht unverändert durch — sonst wäre es keins mehr.**
   *
   * Der Weg unten zeichnet das Bild in ein Canvas und schreibt ein JPEG
   * daraus. Bei einem animierten GIF überlebt davon genau ein Bild: das
   * erste. Aus einem Zwinkern wird ein Standbild, und niemand sieht dem
   * Ergebnis an, woran es lag.
   *
   * Verkleinern ließe sich ein GIF nur, indem man jedes Einzelbild rechnet
   * und die Datei neu zusammensetzt -- ein eigener Kodierer im Browser.
   * Stattdessen gilt für GIFs die rohe Dateigröße: der Composer sagt vorher,
   * wenn sie zu groß ist.
   */
  if (istGif(original)) {
    const masse = { width: bitmap.width, height: bitmap.height }
    bitmap.close?.()
    return { blob: new Blob([original], { type: 'image/gif' }), ...masse, aufnahme, thumb: null, hdr: false }
  }

  /*
   * **Das Vorschaubild wird hier gerechnet und nicht in einem der Zweige
   * unten.** Die Bitmap wird in jedem Zweig geschlossen; wer sie danach noch
   * einmal braucht, müsste die Datei ein zweites Mal dekodieren. Einmal
   * zeichnen für alle Wege — auch für den, der das Original unverändert
   * weiterreicht: gerade dort ist es am nötigsten, denn dann ist die Kachel
   * das volle Bild.
   */
  /*
   * **Ein kleines JPEG bekommt keines** (Codereview D7).
   *
   * `vorschau()` skaliert nie hoch, rechnet aber immer -- und legte damit für
   * ein Foto, das ohnehin schon unter der Vorschaukante liegt, eine **zweite,
   * gleich große Datei** an. Sie zählt gegen das Kontingent, geht über die
   * Leitung und spart nichts.
   *
   * Die Bedingung ist dieselbe wie unten beim unveränderten Weg, um eine
   * Kante erweitert: schon JPEG **und** kleiner als die Vorschaukante. Dann
   * ist das Original seine eigene Vorschau, `thumb_url` bleibt leer, und die
   * Kachel zeigt es wie vor #57.
   */
  const thumbEdge = limits?.thumbEdge ?? THUMB_EDGE
  const thumb = isJpeg(original) && longest <= thumbEdge
    ? null
    : await vorschau(bitmap, thumbEdge, space, profile)
  // Every HDR result goes through here, so that its thumbnail keeps the map
  // as well (1.184.0, `thumbWithMap()`).
  const sourceWidth = bitmap.width
  const hdrOut = (done: Prepared): Promise<Prepared> =>
    thumbWithMap(done, Math.min(1, thumbEdge / longest) * sourceWidth)

  /*
   * Klein genug und schon JPEG: unverändert weiterreichen.
   *
   * **Aber nur aufrecht** (Codereview D3). Ein Foto mit einer Drehmarke geht
   * hier sonst mit ihr hinaus, und der Server liest die Maße aus dem
   * Dateikopf -- der steht bei einer Hochkantaufnahme quer. Drei Folgen, alle
   * beobachtbar: die Galerie reserviert das falsche Seitenverhältnis, das
   * Vorschaubild daneben ist aufrecht gezeichnet und passt nicht dazu, und im
   * PDF liegt das Foto auf der Seite, weil `PdfBook::jpegFor()` keine Marke
   * kennt.
   *
   * Neuzeichnen löst alle drei auf einmal: `createImageBitmap` richtet nach
   * der Marke aus, der Weg unten schreibt das aufrechte Bild, und
   * `keepExif()` setzt die Marke dabei auf 1. Es kostet einen Durchgang für
   * genau die Fotos, die ihn brauchen.
   */
  // **And a gain map goes out only with the switch** (1.179.0, #155): an
  // HDR JPEG that is small enough is redrawn when `hdr_photo` is off, so
  // that the map is gone; with it on it keeps the map as it is.
  const gainMap = isJpeg(picture) && namesGainMap(picture)
  if (
    isJpeg(original)
    && longest <= maxEdge
    && original.byteLength <= KEEP_ORIGINAL_BYTES
    && ausrichtung(original) === 1
    && (!gainMap || limits?.hdrPhoto === true)
  ) {
    const masse = { width: bitmap.width, height: bitmap.height }
    bitmap.close?.()
    const done = { blob: new Blob([original], { type: 'image/jpeg' }), ...masse, aufnahme, thumb, hdr: gainMap }
    return gainMap ? hdrOut(done) : done
  }

  const scale = Math.min(1, maxEdge / longest)
  const width = Math.max(1, Math.round(bitmap.width * scale))
  const height = Math.max(1, Math.round(bitmap.height * scale))

  const canvas = document.createElement('canvas')
  canvas.width = width
  canvas.height = height
  const context = canvas.getContext('2d', { colorSpace: space })
  if (!context) throw new Error(WORTLAUT.technik.fehler.bildNichtVerkleinern)
  const drawnIn = contextSpace(context)
  aufWeiss(context, width, height)
  context.drawImage(bitmap, 0, 0, width, height)
  bitmap.close?.()

  const kodiere = (q: number) => new Promise<Blob | null>((resolve) => canvas.toBlob(resolve, 'image/jpeg', q))
  let gezeichnet = await kodiere(quality)
  if (!gezeichnet) throw new Error(WORTLAUT.technik.fehler.bildNichtUmwandeln)
  /*
   * **A sphere fits the byte limit by its quality, not by its edge**
   * (1.173.0): at 8192 points it can weigh more than `image_max_mb`, and
   * the edge is what makes it worth showing. Down in steps to 50 %; past
   * that the server says it is too large, as for any photo.
   */
  if (sphere && limits?.maxBytes !== undefined) {
    for (let q = quality - 0.08; gezeichnet.size > limits.maxBytes && q >= 0.5; q -= 0.08) {
      gezeichnet = (await kodiere(q)) ?? gezeichnet
    }
  }

  const fertig = await keepExif(await withProfile(gezeichnet, drawnIn, profile, true), picture, aufnahme)
  if (sphere) {
    // The redrawn JPEG lost the camera's flag; it is written again, with the
    // new size (`withSphereXmp()`). A sphere keeps no gain map: its viewer
    // draws the flat picture in WebGL, and two XMP blocks would be a second
    // thing for every reader of the first.
    const bytes = withSphereXmp(new Uint8Array(await fertig.arrayBuffer()), width, height)
    return { blob: new Blob([bytes], { type: 'image/jpeg' }), width, height, aufnahme, thumb, hdr: false }
  }
  if (gainMap && limits?.hdrPhoto === true) {
    // **The map is reduced by the same factor and put back** (1.179.0,
    // `lib/gainMap.ts`). A failure costs the map and never the photo.
    const mitKarte = await reduceWithGainMap(picture, fertig, scale).catch((fehler: unknown) => {
      notiere(`Foto ohne Gain Map: ${fehler instanceof Error ? fehler.message : String(fehler)}`)
      return null
    })
    if (mitKarte !== null) return hdrOut({ blob: mitKarte, width, height, aufnahme, thumb, hdr: true })
  }
  if (heif !== null && limits?.hdrPhoto === true && !sphere) {
    // **A HEIC becomes an Ultra HDR JPEG** (1.181.0, `lib/heifGainMap.ts`):
    // its map is decoded on its own and put behind the drawn base with the
    // `tmap` metadata. A failure costs the map and never the photo.
    const mitKarte = await heifToHdrJpeg(heif, fertig, scale, limits.heifDecode).catch((fehler: unknown) => {
      notiere(`Foto ohne Gain Map aus HEIC: ${fehler instanceof Error ? fehler.message : String(fehler)}`)
      return null
    })
    if (mitKarte !== null) {
      notiere('Foto: HDR aus HEIC, Gain Map übernommen')
      return hdrOut({ blob: mitKarte, width, height, aufnahme, thumb, hdr: true })
    }
  }
  return { blob: fertig, width, height, aufnahme, thumb, hdr: false }
}

/**
 * A gain map drawn at the base picture's scale, as a JPEG of its own. Into an
 * sRGB canvas: a map holds gains and no colours, and a P3 canvas would
 * convert them.
 */
async function drawnMap(bitmap: ImageBitmap, scale: number): Promise<Uint8Array | null> {
  const width = Math.max(1, Math.round(bitmap.width * scale))
  const height = Math.max(1, Math.round(bitmap.height * scale))
  const canvas = document.createElement('canvas')
  canvas.width = width
  canvas.height = height
  const context = canvas.getContext('2d', { colorSpace: 'srgb' })
  if (!context) return null
  context.drawImage(bitmap, 0, 0, width, height)
  const drawn = await new Promise<Blob | null>((resolve) => canvas.toBlob(resolve, 'image/jpeg', 0.85))
  return drawn === null ? null : new Uint8Array(await drawn.arrayBuffer())
}

const decodeHeif = (heif: Uint8Array<ArrayBuffer>): Promise<ImageBitmap> =>
  createImageBitmap(new Blob([heif], { type: 'image/heic' }), { imageOrientation: 'from-image' })

/**
 * A HEIC's gain map behind the drawn base (1.181.0): the file goes to the
 * HEIF decoder once more with the map as its primary picture, the map is
 * reduced by the base's factor, and the `tmap` metadata go along. The map's
 * rotation is the base's, because the decoder applies the same `irot`.
 */
export async function heifToHdrJpeg(
  heif: HeifGainMap,
  base: Blob,
  scale: number,
  decode: (heif: Uint8Array<ArrayBuffer>) => Promise<ImageBitmap> = decodeHeif,
): Promise<Blob | null> {
  const meta: MapMetadata | null = metadataFromIso(heif.iso)
  if (meta === null) throw new Error('tmap nicht lesbar')
  const bitmap = await decode(heif.asPrimary())
  try {
    const map = await drawnMap(bitmap, scale)
    if (map === null) return null
    return new Blob([withGainMap(new Uint8Array(await base.arrayBuffer()), map, meta)], { type: 'image/jpeg' })
  } finally {
    bitmap.close?.()
  }
}

/**
 * The gain map of the original, drawn at the base picture's scale, behind
 * the reduced base. The map is a JPEG of its own and is decoded like one;
 * its values are a gamma-coded gain and are resampled like a picture --
 * which is what every viewer does with a map at another size than the base.
 */
async function reduceWithGainMap(original: Uint8Array, base: Blob, scale: number): Promise<Blob | null> {
  const parts = splitGainMap(original)
  if (parts === null) return null
  const bitmap = await createImageBitmap(new Blob([parts.map.slice()], { type: 'image/jpeg' }))
  try {
    const map = await drawnMap(bitmap, scale)
    if (map === null) return null
    const bytes = withGainMap(new Uint8Array(await base.arrayBuffer()), map, parts.meta)
    return new Blob([bytes], { type: 'image/jpeg' })
  } finally {
    bitmap.close?.()
  }
}

/**
 * **The thumbnail of an HDR photo carries its gain map too** (1.184.0).
 *
 * From use: "manchmal funktionierts, dann wieder mal nicht". The thumbnail
 * was drawn on a canvas and lost the map, and every place that shows it (a
 * post with two or more photos, the media page, the map pins) showed the
 * same photo as SDR that a post with one photo and the large view showed
 * as HDR. The map comes from the finished photo, whatever road it took
 * (an unchanged JPEG, a reduced one, a HEIC, a RAW's preview), and is
 * reduced to the thumbnail's width with the photo's metadata. A failure
 * costs the map and never the thumbnail.
 *
 * `thumbWidth` is the thumbnail's width before rounding, in the same units
 * as the photo's: the factor from the photo to it.
 */
async function thumbWithMap(done: Prepared, thumbWidth: number): Promise<Prepared> {
  if (!done.hdr || done.thumb === null) return done
  const photo = new Uint8Array(await done.blob.arrayBuffer())
  const thumb = await reduceWithGainMap(photo, done.thumb, thumbWidth / done.width).catch((fehler: unknown) => {
    notiere(`Vorschaubild ohne Gain Map: ${fehler instanceof Error ? fehler.message : String(fehler)}`)
    return null
  })
  if (thumb === null) {
    notiere('Vorschaubild ohne Gain Map: die Gain Map des Fotos war nicht lesbar')
    return done
  }
  return { ...done, thumb }
}

/**
 * Ein Vorschaubild aus einer schon dekodierten Bitmap.
 *
 * **Ohne EXIF, und das mit Absicht.** Ort und Zeit stehen am Original; sie ein
 * zweites Mal mitzuschicken hieße, den Aufnahmeort an einer Datei abzulegen,
 * die niemand herunterlädt — und wäre eine zweite Stelle, die stimmen muss.
 *
 * **Und nie fatal.** Scheitert es, bleibt es beim Original in der Kachel:
 * langsamer, aber vollständig. Ein Foto, das gar nicht durchgeht, weil sein
 * Bildchen nicht gerechnet werden konnte, wäre der schlechtere Handel.
 */
/**
 * A thumbnail from a finished JPEG -- for a picture that was not a file to
 * begin with, the drawn map of the day card (1.3.0). Same size and quality
 * as for a photo, same "never fatal".
 */
export async function vorschauAus(blob: Blob, kante: number = THUMB_EDGE): Promise<Blob | null> {
  try {
    const bitmap = await createImageBitmap(blob)
    try {
      return await vorschau(bitmap, kante)
    } finally {
      bitmap.close?.()
    }
  } catch {
    return null
  }
}

/**
 * White under the picture, before it is drawn (1.78.0) -- for both the photo
 * and its thumbnail, so that the two cannot differ.
 *
 * Everything here ends as a JPEG, and a JPEG knows no transparency: a PNG or
 * WebP with a transparent part came out **black** there, while the preview in
 * the composer (the original file) still showed it light. White is what a
 * transparent picture looks like on a page, and a JPEG from the camera has
 * nothing transparent to show it through.
 */
function aufWeiss(context: CanvasRenderingContext2D, width: number, height: number): void {
  context.fillStyle = '#ffffff'
  context.fillRect(0, 0, width, height)
}

async function vorschau(bitmap: ImageBitmap, kante: number, space: CanvasSpace = 'srgb', profile: Uint8Array | null = null): Promise<Blob | null> {
  try {
    const longest = Math.max(bitmap.width, bitmap.height)
    const scale = Math.min(1, kante / longest)
    const width = Math.max(1, Math.round(bitmap.width * scale))
    const height = Math.max(1, Math.round(bitmap.height * scale))

    const canvas = document.createElement('canvas')
    canvas.width = width
    canvas.height = height
    const context = canvas.getContext('2d', { colorSpace: space })
    if (!context) return null
    aufWeiss(context, width, height)
    context.drawImage(bitmap, 0, 0, width, height)

    const drawn = await new Promise<Blob | null>((resolve) =>
      canvas.toBlob(resolve, 'image/jpeg', THUMB_QUALITY),
    )
    return drawn === null ? null : await withProfile(drawn, contextSpace(context), profile, false)
  } catch {
    return null
  }
}

/**
 * The drawn JPEG with the profile of its canvas (1.180.0): nothing for sRGB,
 * and for Display P3 the encoder's own profile where it wrote one, else the
 * original's. `say` puts the way into the diagnosis once per photo.
 */
async function withProfile(drawn: Blob, space: CanvasSpace, profile: Uint8Array | null, say: boolean): Promise<Blob> {
  if (space !== 'display-p3' || profile === null) return drawn
  const bytes = new Uint8Array(await drawn.arrayBuffer())
  const own = hasIcc(bytes)
  if (say) notiere(`Foto: Display P3 behalten, Profil ${own ? 'vom Browser' : 'eingesetzt'}`)
  return own ? drawn : new Blob([withIcc(bytes, profile)], { type: 'image/jpeg' })
}

/**
 * Aufnahmeort und -zeit aus einem fertigen Blob.
 *
 * Für den Ausgangskorb: dort liegt das verkleinerte Bild, und der EXIF-Block
 * steckt noch darin (siehe `keepExif`). Ohne diesen Weg verlöre ein offline
 * geschriebener Beitrag genau die Angaben, um die es geht — und offline
 * geschrieben wird auf einer Reise oft.
 */
export async function aufnahmeAus(
  blob: Blob,
  reiseVersatz: Uhr = null,
): Promise<Aufnahme> {
  try {
    return readAufnahme(new Uint8Array(await blob.arrayBuffer()), reiseVersatz)
  } catch {
    return { at: null, lat: null, lon: null, tz_offset_min: null, befund: 'unlesbar' }
  }
}

/**
 * Die Angaben als Kopfzeilen für den Upload.
 *
 * Als Kopfzeilen und nicht im Rumpf, weil der Rumpf die Bilddatei ist. Was
 * fehlt, wird weggelassen — eine leere Kopfzeile wäre eine Behauptung über
 * nichts, und der Server müsste sie wieder auseinandernehmen.
 */
export function aufnahmeKopf(aufnahme: Aufnahme | undefined): Record<string, string> {
  const kopf: Record<string, string> = {}
  if (!aufnahme) return kopf
  if (aufnahme.at !== null) kopf['x-aufnahme-zeit'] = aufnahme.at
  // The zone (1.87.0): what the file says, with its offset -- or, without
  // one, the camera's wall clock, which the server reads in the trip's zone
  // course. The server decides, so the outbox (which never knew the trip's
  // clock) uploads the same as the form.
  if (aufnahme.tz_offset_min !== null) {
    kopf['x-aufnahme-versatz'] = String(aufnahme.tz_offset_min)
    kopf['x-aufnahme-zone'] = 'aufnahme'
  } else if ((aufnahme.wanduhr ?? null) !== null) {
    kopf['x-aufnahme-wanduhr'] = aufnahme.wanduhr!
  }
  if (aufnahme.lat !== null && aufnahme.lon !== null) {
    kopf['x-aufnahme-ort'] = `${aufnahme.lat},${aufnahme.lon}`
  }
  return kopf
}

/**
 * Ob die Datei sich sofort als Vorschau zeigen lässt.
 *
 * Für die Vorschau vor dem Verkleinern: eine HEIC-Datei vom iPhone zeigt nur
 * Safari an, in Chrome und Firefox bliebe die Kachel schwarz. Dort wird auf
 * das umgewandelte Bild gewartet -- ein paar Zehntelsekunden später, dafür
 * ohne Loch.
 */
export function canPreviewDirectly(file: File): boolean {
  return /^image\/(jpeg|png|webp|gif)$/.test(file.type)
}

/**
 * Nimmt den EXIF-Block des Originals in das neue Bild mit.
 *
 * Geht nur, wenn das Original ein JPEG war. Eine HEIC-Datei vom iPhone trägt
 * ihre Angaben in einem anderen Behälter, an den der Browser nicht
 * heranlässt; dort bleibt es beim reinen Bild. Auf dem iPhone gibt die
 * Dateiauswahl allerdings meist ohnehin ein umgewandeltes JPEG heraus — und
 * das trägt die Angaben mit.
 */
async function keepExif(
  gezeichnet: Blob,
  original: Uint8Array,
  aufnahme: Aufnahme,
): Promise<Blob> {
  // **Aus einem JPEG wird übernommen, aus allem anderen geschrieben.**
  //
  // Übernehmen ist das Bessere, wo es geht: der Block trägt dann auch Kamera,
  // Belichtung und alles Übrige. Aus einem DNG geht es nicht -- dessen
  // Verzeichnisse verweisen auf Bilddaten von vielen Megabyte, und ein
  // APP1-Segment fasst 64 kB. Dann entsteht ein kleiner neuer aus dem, was
  // gelesen wurde.
  const vorhanden = findExif(original)
  const block = vorhanden ? uprightExif(vorhanden) : baueExif(aufnahme)
  if (!block) return gezeichnet

  try {
    const bytes = new Uint8Array(await gezeichnet.arrayBuffer())
    return new Blob([withExif(bytes, block)], { type: 'image/jpeg' })
  } catch {
    // Lieber ein Foto ohne Angaben als gar keines.
    return gezeichnet
  }
}

/**
 * Bildpunkte aus der Datei holen.
 *
 * `imageOrientation: 'from-image'` richtet das Bild nach der EXIF-Marke aus --
 * sonst läge ein Hochkant-Foto vom Telefon quer. Deshalb wird die Marke beim
 * Übernehmen auf „aufrecht" gesetzt: gedreht wurde schon.
 */
async function toBitmap(file: Blob): Promise<ImageBitmap> {
  if ('createImageBitmap' in window) {
    try {
      return await createImageBitmap(file, { imageOrientation: 'from-image' })
    } catch {
      // Weiter mit dem Umweg unten.
    }
  }

  const url = URL.createObjectURL(file)
  try {
    const image = new Image()
    await new Promise<void>((resolve, reject) => {
      image.onload = () => resolve()
      image.onerror = () => reject(new Error(WORTLAUT.technik.fehler.bildNichtOeffnen))
      image.src = url
    })
    return await createImageBitmap(image)
  } finally {
    URL.revokeObjectURL(url)
  }
}

/** Ob die Datei überhaupt ein Bild ist -- Videos kommen später. */
export function isImage(file: File): boolean {
  return (
    file.type.startsWith('image/') ||
    // Ohne `type` -- das passiert über die Dateien-App -- entscheidet der Name.
    // `dng` gehört dazu, seit eine ProRAW-Aufnahme im Betrieb aufschlug.
    /\.(hei[cf]|jpe?g|png|webp|gif|dng|tiff?)$/i.test(file.name)
  )
}
